import { useDebouncedCallback } from '@react-hookz/web'
import { useCallback, useRef, useEffect } from 'react'
import { useHistory } from 'react-router-dom'

interface AutoSaveOptions {
  delay?: number
  maxWait?: number
  saveOnNavigate?: boolean
}

export function useAutoSave(
  saveFunction: () => void,
  deps: any[],
  { delay = 2000, maxWait, saveOnNavigate = true }: AutoSaveOptions = {},
) {
  const history = useHistory()
  const isAutoSaving = useRef(false)

  // Debounced save function
  const debouncedSaveFunction = useDebouncedCallback(
    () => {
      Promise.resolve(saveFunction()).finally(() => {
        isAutoSaving.current = false
      })
    },
    deps,
    delay,
    maxWait,
  )

  // Wrapped save function to ensure isAutoSaving is set correctly
  const wrappedSaveFunction = useCallback(() => {
    isAutoSaving.current = true
    debouncedSaveFunction()
  }, [debouncedSaveFunction])

  // Effect to handle immediate saving when navigating within the app
  useEffect(() => {
    if (!saveOnNavigate) {
      return
    }
    return history.listen((_, action) => {
      if (action === 'PUSH' || action === 'REPLACE') {
        saveFunction()
      }
    })
  }, [history, saveFunction, saveOnNavigate])

  // Effect for handling before unload
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isAutoSaving.current) {
        event.preventDefault()
        event.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  return wrappedSaveFunction
}
